import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import dayjs from 'dayjs';

import { isPresent } from 'services/operators';
import { DATE_FORMAT } from 'models/constants/input.constants';
import { ApplicationConfigService } from 'services/application-config.service';
import { createRequestOption } from 'app/shared/util/request-util';
import { getRefAdrIdentifier, IRefAdr } from '../models/ref-adr.model';

export type EntityResponseType = HttpResponse<IRefAdr>;
export type EntityArrayResponseType = HttpResponse<IRefAdr[]>;

@Injectable({ providedIn: 'root' })
export class RefAdrService {
  protected resourceUrl: string;

  constructor(protected http: HttpClient, protected applicationConfigService: ApplicationConfigService) {
    this.resourceUrl = this.applicationConfigService.getEndpointFor('api/ref-adrs');
  }

  create(refAdr: IRefAdr): Observable<EntityResponseType> {
    const copy = this.convertDateFromClient(refAdr);
    return this.http.post<IRefAdr>(this.resourceUrl, copy, { observe: 'response' }).pipe(map((res: EntityResponseType) => this.convertDateFromServer(res)));
  }

  update(refAdr: IRefAdr): Observable<EntityResponseType> {
    const copy = this.convertDateFromClient(refAdr);
    return this.http
      .put<IRefAdr>(`${this.resourceUrl}/${getRefAdrIdentifier(refAdr)}`, copy, { observe: 'response' })
      .pipe(map((res: EntityResponseType) => this.convertDateFromServer(res)));
  }

  partialUpdate(refAdr: IRefAdr): Observable<EntityResponseType> {
    const copy = this.convertDateFromClient(refAdr);
    return this.http
      .patch<IRefAdr>(`${this.resourceUrl}/${getRefAdrIdentifier(refAdr)}`, copy, { observe: 'response' })
      .pipe(map((res: EntityResponseType) => this.convertDateFromServer(res)));
  }

  find(id: number): Observable<EntityResponseType> {
    return this.http.get<IRefAdr>(`${this.resourceUrl}/${id}`, { observe: 'response' }).pipe(map((res: EntityResponseType) => this.convertDateFromServer(res)));
  }

  query(req?: any): Observable<EntityArrayResponseType> {
    const options = createRequestOption(req);
    return this.http
      .get<IRefAdr[]>(this.resourceUrl, {
        params: options,
        observe: 'response',
      })
      .pipe(map((res: EntityArrayResponseType) => this.convertDateArrayFromServer(res)));
  }

  delete(id: number): Observable<HttpResponse<{}>> {
    return this.http.delete(`${this.resourceUrl}/${id}`, { observe: 'response' });
  }

  addRefAdrToCollectionIfMissing(refAdrCollection: IRefAdr[], ...refAdrsToCheck: (IRefAdr | null | undefined)[]): IRefAdr[] {
    const refAdrs: IRefAdr[] = refAdrsToCheck.filter(isPresent);
    if (refAdrs.length > 0) {
      const refAdrCollectionIdentifiers = refAdrCollection.map(refAdrItem => getRefAdrIdentifier(refAdrItem));
      const refAdrsToAdd = refAdrs.filter(refAdrItem => {
        const refAdrIdentifier = getRefAdrIdentifier(refAdrItem);
        if (refAdrIdentifier == null || refAdrCollectionIdentifiers.includes(refAdrIdentifier)) {
          return false;
        }
        refAdrCollectionIdentifiers.push(refAdrIdentifier);
        return true;
      });
      return [...refAdrsToAdd, ...refAdrCollection];
    }
    return refAdrCollection;
  }

  protected convertDateFromClient(refAdr: IRefAdr): IRefAdr {
    return Object.assign({}, refAdr, {
      startValidity: refAdr.startValidity?.isValid() ? refAdr.startValidity.format(DATE_FORMAT) : undefined,
      endValidity: refAdr.endValidity?.isValid() ? refAdr.endValidity.format(DATE_FORMAT) : undefined,
    });
  }

  protected convertDateFromServer(res: EntityResponseType): EntityResponseType {
    if (res.body) {
      res.body.startValidity = res.body.startValidity ? dayjs(res.body.startValidity) : undefined;
      res.body.endValidity = res.body.endValidity ? dayjs(res.body.endValidity) : undefined;
    }
    return res;
  }

  protected convertDateArrayFromServer(res: EntityArrayResponseType): EntityArrayResponseType {
    if (res.body) {
      res.body.forEach((refAdr: IRefAdr) => {
        refAdr.startValidity = refAdr.startValidity ? dayjs(refAdr.startValidity) : undefined;
        refAdr.endValidity = refAdr.endValidity ? dayjs(refAdr.endValidity) : undefined;
      });
    }
    return res;
  }
}
